/* eslint-disable no-undef */
// Minimal DOM guards for RN TS compile; actual usage is on web only
declare const document: any;
import React, { useCallback, useRef, useState } from 'react';
import { View, StyleSheet, Pressable, Linking } from 'react-native';
import { Event } from '@/types/residents/event';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { RNButton } from '@/components/common/residents/desktop/Button';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { buildGoogleCalendarUrl, buildICS } from '@/utils/residents/events/calendar';
import type { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { logger } from '@/utils/common/logger';

interface Props { calendarEvent: Event }

export const AddToCalendarButton: React.FC<Props> = ({ calendarEvent }) => {
  const DESKTOP_COLORS = useDesktopColors();
  const btnRef = useRef<View | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const openMenu = useCallback(() => {
    if (!btnRef.current) return;
    try {
      const node: any = btnRef.current;
      const rect = node.getBoundingClientRect ? node.getBoundingClientRect() : null;
      if (rect) {
        setAnchor({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
      } else {
        // Fallback for native if ever called
        setAnchor({ x: 0, y: 0, w: 200, h: 32 });
      }
      setIsOpen(true);
    } catch {
      setAnchor({ x: 0, y: 0, w: 200, h: 32 });
      setIsOpen(true);
    }
  }, []);

  const onRequestClose = useCallback(() => setIsOpen(false), []);

  const downloadICS = useCallback(() => {
    const ics = buildICS(calendarEvent);
    // Use data URI to avoid DOM lib typing in native builds
    const fileName = `${(calendarEvent.name || 'event').replace(/\s+/g, '_')}.ics`;
    const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
    try {
      const a = document.createElement('a');
      a.href = dataUri;
      a.setAttribute('download', fileName);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      // no-op on environments without DOM
    }
    setIsOpen(false);
  }, [calendarEvent]);

  const openGoogleCalendar = useCallback(() => {
    const url = buildGoogleCalendarUrl(calendarEvent);
    try {
      // Prefer opening new tab; fallback to RN Linking
      (globalThis as any)?.open?.(url, '_blank');
    } catch (e) {
      logger.warn('addToCalendarWindowOpenFailed', e);
    }
    Linking.openURL(url).catch(() => {});
    setIsOpen(false);
  }, [calendarEvent]);

  return (
    <View style={styles.root} ref={btnRef as any}>
      <RNButton
        title="Add to Calendar"
        onPress={openMenu}
        variant="link"
        size="sm"
        textStyle={{ color: DESKTOP_COLORS.BUTTON_TERTIARY, fontWeight: '700' }}
        icon={<RESIDENTS_SVG_COMPONENTS.CALENDAR width={16} height={16} color={DESKTOP_COLORS.BUTTON_TERTIARY} />}
        style={{ paddingVertical: 0, paddingHorizontal: 0 }}
      />
      <AnchoredDropdown isOpen={isOpen} anchor={anchor} onRequestClose={onRequestClose} width={240}>
        <View style={styles.menu}>
          <Pressable style={styles.menuItem} onPress={openGoogleCalendar}>
            <RESIDENTS_SVG_COMPONENTS.CALENDAR width={16} height={16} color={DESKTOP_COLORS.TEXT_PRIMARY} />
            <View style={{ width: 8 }} />
            <RNButton title="Google Calendar" onPress={openGoogleCalendar} variant="link" size="sm" textStyle={{ color: DESKTOP_COLORS.TEXT_PRIMARY }} />
          </Pressable>
          <Pressable style={styles.menuItem} onPress={downloadICS}>
            <RESIDENTS_SVG_COMPONENTS.CALENDAR width={16} height={16} color={DESKTOP_COLORS.TEXT_PRIMARY} />
            <View style={{ width: 8 }} />
            <RNButton title="iCal (.ics)" onPress={downloadICS} variant="link" size="sm" textStyle={{ color: DESKTOP_COLORS.TEXT_PRIMARY }} />
          </Pressable>
          <Pressable style={styles.menuItem} onPress={downloadICS}>
            <RESIDENTS_SVG_COMPONENTS.CALENDAR width={16} height={16} color={DESKTOP_COLORS.TEXT_PRIMARY} />
            <View style={{ width: 8 }} />
            <RNButton title="Outlook (.ics)" onPress={downloadICS} variant="link" size="sm" textStyle={{ color: DESKTOP_COLORS.TEXT_PRIMARY }} />
          </Pressable>
        </View>
      </AnchoredDropdown>
    </View>
  );
};

// helpers moved to utils/residents/events/calendar

const styles = StyleSheet.create({
  root: { position: 'relative' },
  menu: {
    paddingVertical: 8,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
});

export default AddToCalendarButton;


